import React from "react";
import {
    Form,
    Input,
    Button,
    Select,
    DatePicker,
    Checkbox,
    Tooltip,
    message,
} from "antd";
import { InfoCircleFilled } from "@ant-design/icons";
import { PRACTICE_AREAS } from "common/Constants";
import styled from "styled-components";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import dayjs from "dayjs";

const { Option, OptGroup } = Select;
const { TextArea } = Input;

const FormBody = styled.div`
  padding-bottom: 80px;

  .form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 60px;
    margin-bottom: 0;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 16px;

    label {
      font-size: 14px;
      font-weight: 500;
      color: #333;
      .required { color: red; margin-left: 2px; }
    }
  }

  .ant-input,
  .ant-input-affix-wrapper {
    height: 34px !important;
    font-size: 13px !important;
    border-radius: 3px !important;
    border: 1px solid #d9d9d9 !important;
    color: #333 !important;
  }

  .ant-select-selector {
    height: 34px !important;
    font-size: 13px !important;
    border-radius: 3px !important;
    border: 1px solid #d9d9d9 !important;
    display: flex !important;
    align-items: center !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    font-size: 13px !important;
    line-height: 32px !important;
  }

  .ant-picker {
    height: 34px !important;
    font-size: 13px !important;
    border-radius: 3px !important;
    border: 1px solid #d9d9d9 !important;
    width: 100%;
  }

  textarea.ant-input {
    height: auto !important;
    font-size: 13px !important;
    border-radius: 3px !important;
  }

  .ant-input:focus,
  .ant-input-focused,
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused {
    border-color: #00c48c !important;
    box-shadow: 0 0 0 2px rgba(0, 196, 140, 0.1) !important;
  }

  .ant-select-focused .ant-select-selector {
    border-color: #00c48c !important;
    box-shadow: 0 0 0 2px rgba(0, 196, 140, 0.1) !important;
  }

  .ant-picker:focus,
  .ant-picker-focused {
    border-color: #00c48c !important;
    box-shadow: 0 0 0 2px rgba(0, 196, 140, 0.1) !important;
  }

  /* FIX 8 (checkbox): Green checkbox color */
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #2ecc71 !important;
    border-color: #2ecc71 !important;
  }
  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-input:focus + .ant-checkbox-inner {
    border-color: #2ecc71 !important;
  }
  .ant-checkbox-wrapper:hover .ant-checkbox-inner {
    border-color: #2ecc71 !important;
  }
  .add-btn{
    color: #00685F !important;
  }
`;

// FIX 6: Helper to format date as DD-MM-YYYY (reversed from YYYY-MM-DD)
// This is used in disabledDate callbacks — dates are compared as dayjs objects
// The display format "DD-MM-YYYY" is set via the `format` prop on DatePicker

const ClientAndStakeHolderForMatter = ({
    form,
    clientList = [],
    onClientChange,
    partnerOptions = [],
    onLeadAttorneyChange,
    onReferringAttorneyChange,
    matterNameList = [],
    onLookup, isEditMode = false,       // ← add this
    currentMatterName = "",
    resourceList = [],
    matterStatus
}) => {
    const history = useHistory();
    
    const hasResourceConflict = (newStart, newEnd) => {
        if (!newStart || !newEnd) return [];

        const planStart = dayjs(newStart).startOf("day");
        const planEnd   = dayjs(newEnd).startOf("day");

        return resourceList.filter((r) => {
            const bookedStart = r.startDate
                ? dayjs(r.startDate).startOf("day")
                : null;
            // fall back to endDate if finishDate is absent
            const bookedEndRaw = r.finishDate || r.endDate || null;
            const bookedEnd = bookedEndRaw
                ? dayjs(bookedEndRaw).startOf("day")
                : null;

            if (!bookedStart && !bookedEnd) return false;

            // startOk: resource start must be on or after plan start
            const startOk = bookedStart
                ? bookedStart.isSame(planStart) || bookedStart.isAfter(planStart)
                : true;
            // endOk: resource end must be on or before plan end
            const endOk = bookedEnd
                ? bookedEnd.isSame(planEnd) || bookedEnd.isBefore(planEnd)
                : true;

            // Conflict only when resource dates fall OUTSIDE the plan range
            return !startOk || !endOk;
        });
    };

    const CONFLICT_MSG_KEY = "plan-date-conflict";

    const showConflictMessage = (conflicting) => {
        message.warning({
            key: CONFLICT_MSG_KEY,
            content: `The revised matter timeline conflicts with existing resource allocation dates. Please update the resource booking dates before saving.`,
            duration: 6,
        });
    };

    const handlePlanStartChange = (date) => {
        const endDate = form.getFieldValue("planEndDate");
        const conflicting = hasResourceConflict(date, endDate);
        if (conflicting.length > 0) {
            showConflictMessage();
        }
    };

    const handlePlanEndChange = (date) => {
        const startDate = form.getFieldValue("planStartDate");
        const conflicting = hasResourceConflict(startDate, date);
        if (conflicting.length > 0) {
            showConflictMessage();
        }
    };
    const handleLookup = () => {
        if (onLookup) {
            onLookup();
        } else {
            history.push("/matterLookUpCalendar");
        }
    };
    return (
        <FormBody>
            <Form form={form} layout="vertical">

                {/* Row 1: Matter ID + Proposal ID */}
                <div className="form-row">
                    <div className="form-group">
                        <label>Matter ID</label>
                        <Form.Item name="matterId" noStyle>
                            <Input disabled />
                        </Form.Item>
                    </div>
                    <div className="form-group">
                        <label>Proposal ID</label>
                        <Form.Item name="proposalId" noStyle>
                            <Input />
                        </Form.Item>
                    </div>
                </div>

                {/* Row 2: Client + Job Title */}
                <div className="form-row">
                    <div className="form-group">
                        <label> <span className="required">*</span> Client</label>
                        <Form.Item
                            name="client"
                            rules={[
                                { required: true, message: "Client is required" },
                                {
                                    validator: (_, value) => {
                                        if (!value) return Promise.resolve();

                                        const selectedClient = clientList.find(
                                            (c) => c.bdo_id === value
                                        );

                                        if (selectedClient?.statusid === "0") {
                                            return Promise.reject(
                                                new Error("Client is inactive")
                                            );
                                        }

                                        return Promise.resolve();
                                    },
                                },
                            ]}
                            validateTrigger={["onChange", "onBlur"]}
                        >
                            <Select
                                placeholder="Select client"
                                showSearch
                                optionFilterProp="children"
                                style={{ width: "100%" }}
                                onChange={(value) => {
                                    const found = clientList.find((c) => c.bdo_id === value);

                                    onClientChange?.(found || null);

                                    form.validateFields(["client"]);
                                }}
                            >
                                {clientList.map((c) => (
                                    <Option key={c.bdo_id} value={c.bdo_id}>
                                        {c.companyregisteredname || c.clientname || c.clientCode}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>
                    <div className="form-group">
                        <label><span className="required">*</span> Job Title</label>
                        <Form.Item
                            name="jobTitle"
                            rules={[
                                { required: true, message: "Job Title is required" },
                                {
                                    validator: async (_, value) => {
                                        if (!value) return Promise.resolve();
                                        const normalized = value.trim().toLowerCase();
                                        const currentNormalized = (currentMatterName || "").trim().toLowerCase();
                                        if (isEditMode && currentNormalized && normalized === currentNormalized) {
                                            return Promise.resolve();
                                        }
                                        const isDuplicate = matterNameList.some(
                                            (m) =>
                                                (m.mattername || m.jobTitle || m.information?.jobTitle || m.matter || m.matterName || "")
                                                    .trim()
                                                    .toLowerCase() === normalized
                                        );
                                        if (isDuplicate) {
                                            return Promise.reject(
                                                new Error("This matter name already exists.")
                                            );
                                        }
                                        return Promise.resolve();
                                    },
                                },
                            ]}
                            validateTrigger={["onChange", "onBlur"]}
                            style={{ marginBottom: 0 }}
                        >
                            <Input
                                style={{ width: "100%" }}
                                onChange={() => {
                                    // trigger validation immediately on every keystroke
                                    form.validateFields(["jobTitle"]);
                                }}
                            />
                        </Form.Item>
                    </div>
                </div>

                {/* Row 3: Client Ref + Location */}
                <div className="form-row">
                    <div className="form-group">
                        <label>Client Reference Number</label>
                        <Form.Item name="clientRef" noStyle>
                            <Input style={{ width: "100%" }} />
                        </Form.Item>
                    </div>
                    <div className="form-group">
                        <label>Location</label>
                        <Form.Item name="location" noStyle>
                            <Input style={{ width: "100%" }} />
                        </Form.Item>
                    </div>
                </div>

                {/* Row 4: Practice Area (half width) */}
                <div className="form-row">
                    <div className="form-group">
                        <label>Practice Area</label>
                        <Form.Item name="practiceArea" noStyle>
                            <Select placeholder="Select Practice Area" style={{ width: "100%" }}>
                                {PRACTICE_AREAS.map((group) => (
                                    <OptGroup key={group.label} label={group.label}>
                                        {group.options.map((item) => (
                                            <Option key={item} value={item}>{item}</Option>
                                        ))}
                                    </OptGroup>
                                ))}
                            </Select>
                        </Form.Item>
                    </div>
                    <div />
                </div>

                {/* Objective — full width */}
                <div className="form-group">
                    <label><span className="required">*</span> Objective </label>
                    <Form.Item name="objective" noStyle rules={[{ required: true, message: "Objective is required" }]}>
                        <TextArea rows={4} style={{ width: "100%" }} />
                    </Form.Item>
                </div>

                {/* Row 5: Lead Attorney + Referring Attorney */}
                <div className="form-row">
                    <div className="form-group">
                        <label><span className="required">*</span> Lead Attorney </label>
                        <Form.Item name="leadAttorney" noStyle rules={[{ required: true, message: "Lead Attorney is required" }]}>
                            <Select
                                placeholder="Select Lead Attorney"
                                showSearch
                                optionFilterProp="label"
                                options={partnerOptions}
                                style={{ width: "100%" }}
                                onChange={(val) => onLeadAttorneyChange?.(val)}
                                filterOption={(input, option) =>
                                    (option?.label?.toLowerCase() ?? "").includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </div>
                    <div className="form-group">
                        <label><span className="required">*</span> Referring Attorney </label>
                        <Form.Item name="referringAttorney" noStyle rules={[{ required: true, message: "Referring Attorney is required" }]}>
                            <Select
                                placeholder="Select Referring Attorney"
                                showSearch
                                optionFilterProp="label"
                                options={partnerOptions}
                                style={{ width: "100%" }}
                                onChange={(val) => onReferringAttorneyChange?.(val)}
                                filterOption={(input, option) =>
                                    (option?.label?.toLowerCase() ?? "").includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                    </div>
                </div>

                {/* Row 6: Plan Start + Plan End */}
                {/* FIX 6: format="DD-MM-YYYY" ensures dates display as day-month-year */}
                <div className="form-row">
                    <div className="form-group">
                        <label><span className="required">*</span> Plan Start Date </label>
                        <Form.Item
                            name="planStartDate"
                            noStyle
                            rules={[{ required: true, message: "Plan Start Date is required" }]}
                        >
                            <DatePicker
                                style={{ width: "100%" }}
                                format="DD-MM-YYYY"
                                placeholder="DD-MM-YYYY"
                                disabled={
                                    (matterStatus === "Active" || matterStatus === "Closed")
                                }
                                onChange={handlePlanStartChange}
                                disabledDate={(current) => {       // unchanged
                                    const endDate = form.getFieldValue("planEndDate");
                                    if (!endDate) return false;
                                    return current && current.isAfter(endDate, "day");
                                }}
                            />
                        </Form.Item>
                    </div>
                    <div className="form-group">
                        <label><span className="required">*</span> Plan End Date </label>
                        <Form.Item
                            name="planEndDate"
                            noStyle
                            rules={[{ required: true, message: "Plan End Date is required" }]}
                        >
                            <DatePicker
                                style={{ width: "100%" }}
                                format="DD-MM-YYYY"
                                placeholder="DD-MM-YYYY"
                                onChange={handlePlanEndChange}     // ✅ only this line added
                                disabledDate={(current) => {       // unchanged
                                    const startDate = form.getFieldValue("planStartDate");
                                    if (!startDate) return false;
                                    return current && current.isBefore(startDate, "day");
                                }}
                            />
                        </Form.Item>
                    </div>
                </div>

                {/* Row 7: Legal Filing Deadline + Deadline Met */}
                <div className="form-row">
                    <div className="form-group">
                        <label>
                            Legal Filing Deadline
                            <Tooltip title="The last date by which legal action must be initiated">
                                <InfoCircleFilled className="info-icon" />
                            </Tooltip>
                        </label>
                        {/* FIX 6: format="DD-MM-YYYY" for legal filing deadline too */}
                        <Form.Item name="legalFilingDeadline" noStyle>
                            <DatePicker
                                style={{ width: "100%" }}
                                format="DD-MM-YYYY"
                                placeholder="DD-MM-YYYY"
                            />
                        </Form.Item>
                    </div>
                    <div className="form-group" style={{ flexDirection: "row", alignItems: "center", gap: 8, paddingTop: 22 }}>
                        {/* FIX 8: Green checkbox via CSS above */}
                        <Form.Item name="deadlineMet" valuePropName="checked" noStyle>
                            <Checkbox />
                        </Form.Item>
                        <span style={{ fontSize: 13, color: "#333" }}>Deadline Met</span>
                        <Tooltip title="Check once the case has been filed or the deadline is no longer applicable">
                            <InfoCircleFilled className="info-icon" />
                        </Tooltip>
                    </div>
                </div>

                {/* FIX 7: Lookup button with handler */}
                <div className="lookup-row">
                    <Button
                        className="add-btn"
                        onClick={handleLookup}
                        style={{
                            background: "#deefee",
                            borderColor: "#deefee",
                            color: "#00685F",
                            fontWeight: 500,
                            borderRadius: 4,
                        }}
                    >
                        Look Up
                    </Button>
                </div>

            </Form>
        </FormBody>
    );
};

export default ClientAndStakeHolderForMatter;